import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../api';
import { RNLLoader, useToast, Select, DatePicker } from '../UI';

// ─── Shared styles ────────────────────────────────────────────────────────────
const inp = {
  padding: '8px 10px', borderRadius: 7, border: '1.5px solid var(--border)',
  background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13,
  width: '100%', boxSizing: 'border-box',
};
const lbl = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const btnPrimary = {
  padding: '10px 26px', borderRadius: 9, border: 'none', cursor: 'pointer',
  fontWeight: 700, fontSize: 13, background: '#0b318bff', color: '#fff',
};
const btnSecondary = {
  padding: '10px 18px', borderRadius: 9, border: '1.5px solid var(--border)',
  cursor: 'pointer', fontWeight: 700, fontSize: 13,
  background: 'var(--bg-card)', color: 'var(--text-primary)',
};
const btnDanger = {
  padding: '4px 10px', borderRadius: 6, border: 'none',
  cursor: 'pointer', fontSize: 12, background: '#fee2e2', color: '#dc2626', fontWeight: 600,
};

// ─── ID helpers ───────────────────────────────────────────────────────────────
let _uid = 0;
const uid = () => ++_uid;

const lineFromApi = (li) => ({
  _id:       uid(),
  line_item_id: li.line_item_id,
  material_description: li.material_description || '',
  specification:        li.specification        || '',
  brand_model:          li.brand_model          || '',
  required_quantity:    li.required_quantity    != null ? String(li.required_quantity) : '',
  unit_of_measure:      li.unit_of_measure      || '',
  estimated_unit_rate:  li.estimated_unit_rate  != null ? String(li.estimated_unit_rate) : '',
  estimated_total:      li.estimated_total      != null ? String(li.estimated_total)     : '',
  remarks:              li.remarks              || '',
  _deleted: false,
});

const catFromApi = (cat) => ({
  _id:           uid(),
  category_id:   cat.category_id,
  category_name: cat.category_name || '',
  line_items:    (cat.line_items || []).map(lineFromApi),
  _deleted: false,
});

const newLine = () => ({
  _id: uid(), line_item_id: null,
  material_description: '', specification: '', brand_model: '',
  required_quantity: '', unit_of_measure: '', estimated_unit_rate: '', estimated_total: '',
  remarks: '', _deleted: false,
});
const newCat = () => ({
  _id: uid(), category_id: null,
  category_name: '', line_items: [newLine()], _deleted: false,
});

// ─── Line row ─────────────────────────────────────────────────────────────────
function LineRow({ line, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...line, [k]: e.target.value });

  const handleQty = (e) => {
    const qty = e.target.value;
    const rate = line.estimated_unit_rate;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : line.estimated_total;
    onChange({ ...line, required_quantity: qty, estimated_total: total });
  };
  const handleRate = (e) => {
    const rate = e.target.value;
    const qty = line.required_quantity;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : line.estimated_total;
    onChange({ ...line, estimated_unit_rate: rate, estimated_total: total });
  };

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ padding: '6px' }}><input style={{ ...inp, minWidth: 200 }} value={line.material_description} onChange={set('material_description')} placeholder="Description *" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, minWidth: 110 }} value={line.specification} onChange={set('specification')} placeholder="Spec" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, minWidth: 100 }} value={line.brand_model} onChange={set('brand_model')} placeholder="Brand/Model" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, width: 72, textAlign: 'right' }} type="number" value={line.required_quantity} onChange={handleQty} placeholder="Qty" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, width: 60 }} value={line.unit_of_measure} onChange={set('unit_of_measure')} placeholder="UOM" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, width: 90, textAlign: 'right' }} type="number" value={line.estimated_unit_rate} onChange={handleRate} placeholder="Rate" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, width: 90, textAlign: 'right' }} type="number" value={line.estimated_total} onChange={set('estimated_total')} placeholder="Total" /></td>
      <td style={{ padding: '6px' }}><input style={{ ...inp, minWidth: 90 }} value={line.remarks} onChange={set('remarks')} placeholder="Remarks" /></td>
      <td style={{ padding: '6px', textAlign: 'center' }}>
        <button style={btnDanger} onClick={onRemove} title="Remove line">✕</button>
      </td>
    </tr>
  );
}

// ─── Category block ───────────────────────────────────────────────────────────
function CatBlock({ cat, onUpdate, onRemove, showRemove }) {
  const setName = (e) => onUpdate({ ...cat, category_name: e.target.value });

  const updateLine = (lid, updated) => onUpdate({
    ...cat, line_items: cat.line_items.map(l => l._id === lid ? updated : l),
  });
  const removeLine = (lid) => {
    const l = cat.line_items.find(x => x._id === lid);
    if (l.line_item_id) {
      onUpdate({ ...cat, line_items: cat.line_items.map(x => x._id === lid ? { ...x, _deleted: true } : x) });
    } else {
      onUpdate({ ...cat, line_items: cat.line_items.filter(x => x._id !== lid) });
    }
  };
  const addLine = () => onUpdate({ ...cat, line_items: [...cat.line_items, newLine()] });

  const visible = cat.line_items.filter(l => !l._deleted);
  const total = visible.reduce((s, l) => s + (parseFloat(l.estimated_total) || 0), 0);

  return (
    <div style={{ border: '2px solid black', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ background: '#0b318b11', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '2px solid black' }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Category</span>
        <input style={{ ...inp, flex: 1, fontWeight: 700 }} value={cat.category_name} onChange={setName} placeholder="Category name" />
        {showRemove && (
          <button style={{ ...btnDanger, whiteSpace: 'nowrap' }} onClick={onRemove}>Remove Category</button>
        )}
      </div>
      <div style={{ overflowX: 'auto', padding: '0 0 8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1.5px solid var(--border)', background: 'var(--bg-subtle)' }}>
              {['Description *', 'Specification', 'Brand/Model', 'Qty', 'UOM', 'Unit Rate', 'Est. Total', 'Remarks', ''].map(h => (
                <th key={h} style={{ padding: '6px', textAlign: 'left', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(line => (
              <LineRow
                key={line._id}
                line={line}
                onChange={(updated) => updateLine(line._id, updated)}
                onRemove={() => removeLine(line._id)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px' }} onClick={addLine}>+ Add Line Item</button>
        {total > 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>
            Category Total: ₦{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MREditPage({ row, onBack, onSaved }) {
  const [mr, setMr]             = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [header, setHeader]     = useState({});
  const [categories, setCategories] = useState([]);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const toast = useToast();

  const setH  = (k) => (e) => setHeader(h => ({ ...h, [k]: e.target.value }));
  const setHV = (k) => (v) => setHeader(h => ({ ...h, [k]: v }));

  useEffect(() => {
    api.listDisciplines(0, 200).then(r => setDisciplines(r?.items || r || [])).catch(() => {});
  }, []);

  const loadMR = useCallback(async () => {
    setLoading(true);
    try {
      api.clearApiCache();
      const res = await api.getMaterialRequest(row.request_id);
      const d = res?.data || res;
      setMr(d);
      setHeader({
        building:      d.building      || '',
        discipline_id: d.discipline_id || '',
        revision:      d.revision      || 'R0',
        priority:      d.priority      || 'normal',
        notes:         d.notes         || '',
        requested_date: d.requested_date || '',
      });
      setCategories((d.categories || []).map(catFromApi));
    } catch {
      toast.show('Failed to load request.', 'error');
    } finally {
      setLoading(false);
    }
  }, [row.request_id]);

  useEffect(() => { loadMR(); }, [loadMR]);

  const updateCat   = (cid, updated) => setCategories(cs => cs.map(c => c._id === cid ? updated : c));
  const removeCat   = (cid) => {
    const c = categories.find(x => x._id === cid);
    if (c.category_id) {
      setCategories(cs => cs.map(x => x._id === cid ? { ...x, _deleted: true } : x));
    } else {
      setCategories(cs => cs.filter(x => x._id !== cid));
    }
  };
  const addCat      = () => setCategories(cs => [...cs, newCat()]);

  const handleSave = async () => {
    setError('');
    const visible = categories.filter(c => !c._deleted);
    if (!visible.some(c => c.category_name.trim())) {
      setError('At least one category is required.');
      return;
    }
    setSaving(true);
    try {
      // 1. Update header
      await api.updateMaterialRequest(row.request_id, {
        building:      header.building      || undefined,
        discipline_id: header.discipline_id || undefined,
        revision:      header.revision      || undefined,
        priority:      header.priority      || undefined,
        notes:         header.notes         || undefined,
      });

      // 2. Delete removed categories (cascades line items)
      for (const cat of categories.filter(c => c._deleted && c.category_id)) {
        await api.deleteMRCategory(cat.category_id);
      }

      // 3. Update / add categories and their line items
      for (const cat of categories.filter(c => !c._deleted)) {
        if (cat.category_id) {
          // Existing category — update name
          await api.updateMRCategory(cat.category_id, { category_name: cat.category_name });

          // Delete removed line items
          for (const li of cat.line_items.filter(l => l._deleted && l.line_item_id)) {
            await api.deleteMRLineItem(li.line_item_id);
          }

          // Update existing line items
          for (const li of cat.line_items.filter(l => !l._deleted && l.line_item_id)) {
            await api.updateMRLineItem(li.line_item_id, buildLinePayload(li));
          }

          // Add new line items
          const newLines = cat.line_items.filter(l => !l._deleted && !l.line_item_id && l.material_description.trim());
          if (newLines.length > 0) {
            await api.bulkAddMRLineItems(cat.category_id, newLines.map(buildLinePayload));
          }
        } else {
          // Brand-new category — add it with all its lines
          const lines = cat.line_items.filter(l => !l._deleted && l.material_description.trim()).map(buildLinePayload);
          if (cat.category_name.trim() && lines.length > 0) {
            await api.addMRCategory(row.request_id, { category_name: cat.category_name.trim(), line_items: lines });
          }
        }
      }

      toast.show(`${mr?.request_ref || 'Request'} saved successfully.`, 'success');
      if (onSaved) onSaved();
      else if (onBack) onBack();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Save failed.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <RNLLoader title="Loading request…" subtitle="Fetching data for editing" />;

  const visibleCats = categories.filter(c => !c._deleted);
  const grandTotal = visibleCats.reduce((s, cat) =>
    s + cat.line_items.filter(l => !l._deleted).reduce((cs, l) => cs + (parseFloat(l.estimated_total) || 0), 0),
  0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Back + Title ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            padding: '6px 16px', background: '#e0f2fe', color: '#1e3a8a',
            border: '2px solid #93c5fd', borderRadius: 9999,
            cursor: 'pointer', fontWeight: 700, fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="15 18 9 12 15 6" /></svg>
          Back
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>Edit {mr?.request_ref || 'Request'}</h2>
      </div>

      {/* ── Info banner ── */}
      <div style={{
        background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
        padding: '10px 16px', fontSize: 13, color: '#92400e', lineHeight: 1.6,
      }}>
        <strong>This edits the record only.</strong> The approval stays exactly as it is — no workflow step is reopened and nobody is asked to approve again. If the change is significant enough that the decision no longer stands, use <strong>Recall</strong> instead. Every edit is written to the audit log.
      </div>

      {/* ── Section 1 — Request Header ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid black', borderRadius: 10, padding: '14px 18px' }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Section 1 — Request Header
        </div>
        {/* Read-only info strip */}
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16, padding: '10px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 13 }}>
          {[
            ['Reference',    mr?.request_ref || 'DRAFT'],
            ['Status',       (mr?.status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())],
            ['Project',      mr?.project_name || mr?.project_id || '—'],
            ['Requester',    row.requested_by_name || '—'],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
              <div style={{ fontWeight: 700, color: '#1e3a8a' }}>{val}</div>
            </div>
          ))}
        </div>
        {/* Editable fields */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <div style={lbl}>Building / Block</div>
            <input style={inp} value={header.building} onChange={setH('building')} placeholder="e.g. Block A — Residential Tower" />
          </div>
          <div>
            <Select
              label="Discipline"
              title="Select Discipline"
              value={header.discipline_id}
              onChange={setHV('discipline_id')}
              options={[
                { value: '', label: '— None —', alwaysVisible: true },
                ...disciplines.map(d => ({ value: d.id, label: d.name || d.discipline_name })),
              ]}
            />
          </div>
          <div>
            <div style={lbl}>Revision</div>
            <input style={inp} value={header.revision} onChange={setH('revision')} placeholder="R0" />
          </div>
          <div>
            <Select
              label="Priority"
              title="Select Priority"
              value={header.priority}
              onChange={setHV('priority')}
              options={[
                { value: 'normal', label: 'Normal' },
                { value: 'high',   label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
            />
          </div>
          <div>
            <DatePicker
              label="Request Date"
              value={header.requested_date}
              onChange={setHV('requested_date')}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <div style={lbl}>Notes</div>
            <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={header.notes} onChange={setH('notes')} placeholder="Optional notes…" />
          </div>
        </div>
      </div>

      {/* ── Section 2 — Categories & Line Items ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid black', borderRadius: 10, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 11, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Section 2 — Categories &amp; Line Items
          </div>
          <button style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px' }} onClick={addCat}>
            + Add Category
          </button>
        </div>

        {visibleCats.map(cat => (
          <CatBlock
            key={cat._id}
            cat={cat}
            onUpdate={(updated) => updateCat(cat._id, updated)}
            onRemove={() => removeCat(cat._id)}
            showRemove={visibleCats.length > 1}
          />
        ))}

        {grandTotal > 0 && (
          <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 800, color: '#1e3a8a', marginTop: 8 }}>
            Grand Total: ₦{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        )}
      </div>

      {/* ── Change reason ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid black', borderRadius: 10, padding: '14px 18px' }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Why is this being changed? <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(kept in the audit log)</span>
        </div>
        <textarea
          style={{ ...inp, minHeight: 64, resize: 'vertical' }}
          value={changeReason}
          onChange={(e) => setChangeReason(e.target.value)}
          placeholder="e.g. Unit rate was keyed in wrong at Cost Control."
        />
      </div>

      {/* ── Error + Actions ── */}
      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={btnSecondary} onClick={onBack} disabled={saving}>Cancel</button>
        <button
          style={{ ...btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function buildLinePayload(li) {
  const p = { material_description: li.material_description.trim() };
  if (li.specification?.trim())    p.specification        = li.specification.trim();
  if (li.brand_model?.trim())      p.brand_model          = li.brand_model.trim();
  if (li.required_quantity !== '') p.required_quantity    = parseFloat(li.required_quantity);
  if (li.unit_of_measure?.trim())  p.unit_of_measure      = li.unit_of_measure.trim();
  if (li.estimated_unit_rate !== '') p.estimated_unit_rate = parseFloat(li.estimated_unit_rate);
  if (li.estimated_total !== '')   p.estimated_total      = parseFloat(li.estimated_total);
  if (li.remarks?.trim())          p.remarks              = li.remarks.trim();
  return p;
}
